import { useEffect, useState } from 'react';
import type { SupportTicketDetail as SupportTicketDetailType } from '../../domain/support/types';
import { canCustomerReply } from '../../domain/support/guards';
import { Button, Textarea } from '../../components/ui';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import { formatDateTime } from '../../utils/formatters';
import { TICKET_STATUS_LABELS } from './ticketStatusLabels';

interface SupportTicketDetailProps {
    ticketId: string | null;
    detail: SupportTicketDetailType | null;
    isOpen: boolean;
    isLoading: boolean;
    isSaving: boolean;
    onClose: () => void;
    onReply: (ticketId: string, body: string) => Promise<SupportTicketDetailType | null>;
}

export function SupportTicketDetail({
    ticketId,
    detail,
    isOpen,
    isLoading,
    isSaving,
    onClose,
    onReply,
}: SupportTicketDetailProps) {
    const [replyBody, setReplyBody] = useState('');

    useEffect(() => {
        if (!isOpen) setReplyBody('');
    }, [isOpen]);

    const isDetailReady = detail?.id === ticketId;
    const canReply = isDetailReady && canCustomerReply(detail.status) && replyBody.trim().length > 0;

    const handleReply = async () => {
        if (!ticketId || !canReply) return;
        const updated = await onReply(ticketId, replyBody.trim());
        if (updated) setReplyBody('');
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl">
            <Modal.Header onClose={onClose}>
                {isDetailReady ? detail.subject : 'Ticket'}
            </Modal.Header>
            <Modal.Body>
                {isLoading && (
                    <div className="flex justify-center py-12">
                        <Spinner color="primary" size="lg" />
                    </div>
                )}

                {!isLoading && isDetailReady && (
                    <>
                        <p className="text-sm text-secondary">
                            Status:{' '}
                            <span className="font-medium text-default">
                                {TICKET_STATUS_LABELS[detail.status]}
                            </span>
                        </p>

                        <div className="space-y-3">
                            <p className="text-sm font-medium text-default">Conversa</p>
                            <div className="max-h-72 space-y-3 overflow-y-auto rounded-lg border border-subtle p-3">
                                {detail.messages.map((msg) => (
                                    <div
                                        key={msg.id}
                                        className={
                                            msg.authorRole === 'staff'
                                                ? 'ml-8 rounded-lg bg-primary/10 p-3'
                                                : 'mr-8 rounded-lg bg-surface-subtle p-3'
                                        }
                                    >
                                        <p className="text-xs text-secondary">
                                            {msg.authorRole === 'staff' ? 'Equipe Adlitics' : 'Você'} ·{' '}
                                            {formatDateTime(msg.createdAt)}
                                        </p>
                                        <p className="mt-1 whitespace-pre-wrap text-sm text-default">
                                            {msg.body}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {canCustomerReply(detail.status) && (
                            <Textarea
                                label="Responder"
                                value={replyBody}
                                onChange={(e) => setReplyBody(e.target.value)}
                                placeholder="Escreva sua mensagem..."
                                minRows={4}
                                disabled={isSaving}
                            />
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                    Fechar
                </Button>
                {isDetailReady && canCustomerReply(detail.status) && (
                    <Button onClick={() => void handleReply()} disabled={!canReply || isSaving}>
                        {isSaving ? 'Enviando...' : 'Enviar resposta'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
